import { useState } from "react";
import api, { resolveImageUrl } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../ui/dialog";
import { Plus, Loader2, ImagePlus, Edit2, X, Video } from "lucide-react";

const EMPTY_RIDE = { departure: "", destination: "", departure_date: "", departure_time: "", available_seats: 3, price_per_seat: 2000, vehicle_description: "", notes: "", pickup_location: "" };
const EMPTY_VEHICLE = { brand: "", model: "", year: 2020, color: "", plate_number: "", vehicle_type: "sedan", city: "", price_per_day: 25000, seats: 4, air_conditioning: false, fuel_policy: "excluded", mileage: "", vehicle_condition: "bon_etat", music: false, phone_charger: false, price_city_tour: 0, price_out_of_city: 0, price_per_hour: 0 };

const CAMEROON_CITIES = ["Yaounde", "Douala", "Bafoussam", "Bamenda", "Garoua", "Maroua", "Ngaoundere", "Bertoua", "Ebolowa", "Kribi", "Limbe", "Buea", "Kumba", "Nkongsamba", "Edea", "Loum", "Dschang", "Foumban", "Kousseri", "Mokolo"];

async function uploadImage(file, type = "vehicle") {
  const formData = new FormData();
  formData.append("file", file);
  const endpoint = type === "ride" ? "/upload/ride-image" : "/upload/vehicle-image";
  const { data } = await api.post(endpoint, formData, { headers: { "Content-Type": "multipart/form-data" } });
  return data.path;
}

// --- RIDE DIALOGS ---
export function AddRideDialog({ open, setOpen, disabled, onCreated }) {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_RIDE });
  const [rideImage, setRideImage] = useState(null);
  const [ridePreview, setRidePreview] = useState(null);

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) { alert("L'image ne doit pas depasser 4 Mo"); return; }
    setRideImage(file);
    setRidePreview(URL.createObjectURL(file));
  };
  const removeImage = () => { setRideImage(null); setRidePreview(null); };

  const handleSubmit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      let imageUrl = "";
      if (rideImage) { imageUrl = await uploadImage(rideImage, "ride"); }
      await api.post("/rides", { ...form, image_url: imageUrl });
      setOpen(false); setForm({ ...EMPTY_RIDE }); setRideImage(null); setRidePreview(null); onCreated();
    }
    catch (e) { alert(e.response?.data?.detail || "Erreur"); }
    finally { setLoading(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setRideImage(null); setRidePreview(null); } }}>
      <DialogTrigger asChild><Button disabled={disabled} className="bg-[#FACC15] text-[#0A2540] hover:bg-[#EAB308] font-semibold disabled:opacity-50" data-testid="add-ride-btn"><Plus className="w-4 h-4 mr-1" /> Ajouter un trajet</Button></DialogTrigger>
      <DialogContent data-testid="ride-form-dialog"><DialogHeader><DialogTitle className="text-[#0A2540]" style={{ fontFamily: 'Outfit' }}>Nouveau trajet</DialogTitle></DialogHeader>
        <RideFormFields form={form} setForm={setForm} onSubmit={handleSubmit} loading={loading} submitLabel="Publier le trajet"
          ridePreview={ridePreview} onImageChange={handleImageChange} onRemoveImage={removeImage} />
      </DialogContent>
    </Dialog>
  );
}

export function EditRideDialog({ ride, open, setOpen, onUpdated }) {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ departure: ride.departure || "", destination: ride.destination || "", departure_date: ride.departure_date || "", departure_time: ride.departure_time || "", available_seats: ride.available_seats || 3, price_per_seat: ride.price_per_seat || 2000, vehicle_description: ride.vehicle_description || "", notes: ride.notes || "", pickup_location: ride.pickup_location || "" });
  const existingImg = ride.image_url || "";
  const [rideImage, setRideImage] = useState(null);
  const [ridePreview, setRidePreview] = useState(existingImg ? resolveImageUrl(existingImg) : null);

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) { alert("L'image ne doit pas depasser 4 Mo"); return; }
    setRideImage(file);
    setRidePreview(URL.createObjectURL(file));
  };
  const removeImage = () => { setRideImage(null); setRidePreview(null); };

  const handleSubmit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      let imageUrl = ride.image_url || "";
      if (rideImage) { imageUrl = await uploadImage(rideImage, "ride"); }
      else if (!ridePreview) { imageUrl = ""; }
      await api.put(`/rides/${ride.id}`, { ...form, image_url: imageUrl }); setOpen(false); onUpdated();
    }
    catch (e) { alert(e.response?.data?.detail || "Erreur"); }
    finally { setLoading(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent data-testid="edit-ride-dialog"><DialogHeader><DialogTitle className="text-[#0A2540]" style={{ fontFamily: 'Outfit' }}>Modifier le trajet</DialogTitle></DialogHeader>
        <RideFormFields form={form} setForm={setForm} onSubmit={handleSubmit} loading={loading} submitLabel="Enregistrer" isEdit
          ridePreview={ridePreview} onImageChange={handleImageChange} onRemoveImage={removeImage} />
      </DialogContent>
    </Dialog>
  );
}

function RideFormFields({ form, setForm, onSubmit, loading, submitLabel, isEdit, ridePreview, onImageChange, onRemoveImage }) {
  return (
    <form onSubmit={onSubmit} className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div><Label className="text-[#0A2540] text-xs">Depart</Label><Input value={form.departure} onChange={e => setForm({...form, departure: e.target.value})} placeholder="Yaounde" required className="h-9 border-[#CBD5E1]" data-testid="ride-departure" /></div>
        <div><Label className="text-[#0A2540] text-xs">Destination</Label><Input value={form.destination} onChange={e => setForm({...form, destination: e.target.value})} placeholder="Douala" required className="h-9 border-[#CBD5E1]" data-testid="ride-destination" /></div>
      </div>
      <div><Label className="text-[#0A2540] text-xs">Lieu de prise en charge</Label><Input value={form.pickup_location} onChange={e => setForm({...form, pickup_location: e.target.value})} placeholder="Ex: Carrefour Bastos, face au Total" className="h-9 border-[#CBD5E1]" data-testid="ride-pickup-location" /></div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label className="text-[#0A2540] text-xs">Date</Label><Input type="date" value={form.departure_date} onChange={e => setForm({...form, departure_date: e.target.value})} required className="h-9 border-[#CBD5E1]" data-testid="ride-date" /></div>
        <div><Label className="text-[#0A2540] text-xs">Heure</Label><Input type="time" value={form.departure_time} onChange={e => setForm({...form, departure_time: e.target.value})} required className="h-9 border-[#CBD5E1]" data-testid="ride-time" /></div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label className="text-[#0A2540] text-xs">Places</Label><Input type="number" value={form.available_seats} onChange={e => setForm({...form, available_seats: parseInt(e.target.value) || 1})} min={1} max={10} className="h-9 border-[#CBD5E1]" data-testid="ride-seats" /></div>
        <div><Label className="text-[#0A2540] text-xs">Prix/place (XAF)</Label><Input type="number" value={form.price_per_seat} onChange={e => setForm({...form, price_per_seat: parseInt(e.target.value) || 0})} min={100} className="h-9 border-[#CBD5E1]" data-testid="ride-price" /></div>
      </div>
      <div><Label className="text-[#0A2540] text-xs">Vehicule</Label><Input value={form.vehicle_description} onChange={e => setForm({...form, vehicle_description: e.target.value})} placeholder="Toyota Corolla blanche" className="h-9 border-[#CBD5E1]" data-testid="ride-vehicle-desc" /></div>
      {/* Ride image upload - max 4MB */}
      <div>
        <Label className="text-[#0A2540] text-xs">Photo du trajet (max 4 Mo)</Label>
        {ridePreview ? (
          <div className="relative mt-1 w-full">
            <img src={ridePreview} alt="Apercu trajet" className="w-full h-32 object-cover rounded-lg border border-[#CBD5E1]" />
            <button type="button" onClick={onRemoveImage} className="absolute -top-1 -right-1 w-6 h-6 bg-[#EF4444] text-white rounded-full flex items-center justify-center" data-testid="ride-image-remove">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        ) : (
          <label className="flex flex-col items-center justify-center h-24 mt-1 border-2 border-dashed border-[#CBD5E1] rounded-lg cursor-pointer hover:border-[#0A2540]/40 transition-colors" data-testid="ride-image-upload">
            <ImagePlus className="w-6 h-6 text-[#CBD5E1]" />
            <span className="text-[10px] text-[#64748B] mt-1">Cliquez pour ajouter une photo</span>
            <input type="file" accept="image/*" onChange={onImageChange} className="hidden" />
          </label>
        )}
      </div>
      <Button type="submit" disabled={loading} className="w-full bg-[#0A2540] text-white hover:bg-[#081b2e] font-semibold" data-testid="submit-ride-btn">
        {loading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
        {!loading && (isEdit ? <Edit2 className="w-4 h-4 mr-2" /> : <Plus className="w-4 h-4 mr-2" />)}
        {submitLabel}
      </Button>
    </form>
  );
}

// --- VEHICLE DIALOGS ---
const MAX_VIDEO_BYTES = 25 * 1024 * 1024; // 25 MB
const EMPTY_IMG = [null, null, null, null, null, null];

async function uploadVehicleVideo(vehicleId, videoFile) {
  const formData = new FormData();
  formData.append("file", videoFile);
  await api.post(`/vehicles/${vehicleId}/upload-video`, formData, { headers: { "Content-Type": "multipart/form-data" } });
}

export function AddVehicleDialog({ open, setOpen, disabled, onCreated }) {
  const [loading, setLoading] = useState(false);
  const [images, setImages] = useState([...EMPTY_IMG]); // 6 image slots
  const [previews, setPreviews] = useState([...EMPTY_IMG]);
  const [video, setVideo] = useState(null);
  const [videoPreview, setVideoPreview] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_VEHICLE });

  const handleImageChange = (idx, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const newImages = [...images]; newImages[idx] = file; setImages(newImages);
    const newPreviews = [...previews]; newPreviews[idx] = URL.createObjectURL(file); setPreviews(newPreviews);
  };
  const removeImage = (idx) => {
    const newImages = [...images]; newImages[idx] = null; setImages(newImages);
    const newPreviews = [...previews]; newPreviews[idx] = null; setPreviews(newPreviews);
  };
  const handleVideoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_VIDEO_BYTES) { alert("La vidéo ne doit pas dépasser 25 Mo"); return; }
    setVideo(file);
    setVideoPreview(URL.createObjectURL(file));
  };
  const removeVideo = () => { setVideo(null); setVideoPreview(null); };

  const resetState = () => {
    setForm({ ...EMPTY_VEHICLE });
    setImages([...EMPTY_IMG]); setPreviews([...EMPTY_IMG]);
    setVideo(null); setVideoPreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const urls = [];
      for (const img of images) { if (img) { urls.push(await uploadImage(img)); } }
      const { data: created } = await api.post("/vehicles", { ...form, image_url: urls[0] || "", image_urls: urls });
      if (video && created?.id) {
        try { await uploadVehicleVideo(created.id, video); }
        catch (err) { alert("Vidéo non chargée: " + (err.response?.data?.detail || "erreur")); }
      }
      setOpen(false); resetState(); onCreated();
    } catch (e) { alert(e.response?.data?.detail || "Erreur"); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) resetState(); }}>
      <DialogTrigger asChild><Button disabled={disabled} className="bg-[#FACC15] text-[#0A2540] hover:bg-[#EAB308] font-semibold disabled:opacity-50" data-testid="add-vehicle-btn"><Plus className="w-4 h-4 mr-1" /> Ajouter un vehicule</Button></DialogTrigger>
      <DialogContent data-testid="vehicle-form-dialog" className="max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="text-[#0A2540]" style={{ fontFamily: 'Outfit' }}>Nouveau vehicule</DialogTitle></DialogHeader>
        <VehicleFormFields form={form} setForm={setForm} onSubmit={handleSubmit} loading={loading} submitLabel="Ajouter le vehicule" previews={previews} onImageChange={handleImageChange} onRemoveImage={removeImage} videoPreview={videoPreview} onVideoChange={handleVideoChange} onRemoveVideo={removeVideo} />
      </DialogContent>
    </Dialog>
  );
}

export function EditVehicleDialog({ vehicle, open, setOpen, onUpdated }) {
  const [loading, setLoading] = useState(false);
  const existingUrls = vehicle.image_urls?.length ? vehicle.image_urls : vehicle.image_url ? [vehicle.image_url] : [];
  const [images, setImages] = useState([...EMPTY_IMG]);
  const [previews, setPreviews] = useState([
    existingUrls[0] || null, existingUrls[1] || null, existingUrls[2] || null,
    existingUrls[3] || null, existingUrls[4] || null, existingUrls[5] || null,
  ]);
  const existingVideo = vehicle.video_url || "";
  const [video, setVideo] = useState(null);
  const [videoPreview, setVideoPreview] = useState(existingVideo ? resolveImageUrl(existingVideo) : null);
  const [videoRemoved, setVideoRemoved] = useState(false);
  const [form, setForm] = useState({
    brand: vehicle.brand || "", model: vehicle.model || "", year: vehicle.year || 2020,
    color: vehicle.color || "", plate_number: vehicle.plate_number || "",
    vehicle_type: vehicle.vehicle_type || "sedan", price_per_day: vehicle.price_per_day || 25000,
    seats: vehicle.seats || 4, air_conditioning: vehicle.air_conditioning || false,
    fuel_policy: vehicle.fuel_policy || "excluded", mileage: vehicle.mileage || "",
    vehicle_condition: vehicle.vehicle_condition || "bon_etat",
    music: vehicle.music || false, phone_charger: vehicle.phone_charger || false,
    price_city_tour: vehicle.price_city_tour || 0, price_out_of_city: vehicle.price_out_of_city || 0,
    city: vehicle.city || "", price_per_hour: vehicle.price_per_hour || 0,
    operational_status_manual: vehicle.operational_status_manual || (vehicle.available !== false ? "available" : "unavailable"),
  });

  const handleImageChange = (idx, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ni = [...images]; ni[idx] = file; setImages(ni);
    const np = [...previews]; np[idx] = URL.createObjectURL(file); setPreviews(np);
  };
  const removeImage = (idx) => {
    const ni = [...images]; ni[idx] = null; setImages(ni);
    const np = [...previews]; np[idx] = null; setPreviews(np);
  };
  const handleVideoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_VIDEO_BYTES) { alert("La vidéo ne doit pas dépasser 25 Mo"); return; }
    setVideo(file);
    setVideoPreview(URL.createObjectURL(file));
    setVideoRemoved(false);
  };
  const removeVideo = () => {
    setVideo(null); setVideoPreview(null);
    if (existingVideo) setVideoRemoved(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const urls = [];
      for (let i = 0; i < 6; i++) {
        if (images[i]) { urls.push(await uploadImage(images[i])); }
        else if (previews[i]) { urls.push(previews[i]); }
      }
      await api.put(`/vehicles/${vehicle.id}`, { ...form, image_url: urls[0] || "", image_urls: urls });
      if (video) {
        try { await uploadVehicleVideo(vehicle.id, video); }
        catch (err) { alert("Vidéo non chargée: " + (err.response?.data?.detail || "erreur")); }
      } else if (videoRemoved && existingVideo) {
        try { await api.delete(`/vehicles/${vehicle.id}/video`); } catch (_) {}
      }
      setOpen(false); onUpdated();
    } catch (e) { alert(e.response?.data?.detail || "Erreur"); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent data-testid="edit-vehicle-dialog" className="max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="text-[#0A2540]" style={{ fontFamily: 'Outfit' }}>Modifier le vehicule</DialogTitle></DialogHeader>
        <VehicleFormFields form={form} setForm={setForm} onSubmit={handleSubmit} loading={loading} submitLabel="Enregistrer" isEdit previews={previews} onImageChange={handleImageChange} onRemoveImage={removeImage} videoPreview={videoPreview} onVideoChange={handleVideoChange} onRemoveVideo={removeVideo} />
      </DialogContent>
    </Dialog>
  );
}

function ToggleOption({ label, value, onChange, testId }) {
  return (
    <button type="button" onClick={() => onChange(!value)}
      className={`flex items-center justify-between px-3 py-2 rounded-lg border text-xs font-medium transition-colors ${value ? "bg-[#10B981]/10 border-[#10B981]/40 text-[#10B981]" : "bg-[#F8FAFC] border-[#CBD5E1] text-[#64748B]"}`}
      data-testid={testId}>
      <span>{label}</span>
      <span className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${value ? "border-[#10B981] bg-[#10B981]" : "border-[#CBD5E1]"}`}>
        {value && <span className="w-2 h-2 bg-white rounded-full" />}
      </span>
    </button>
  );
}

function VehicleFormFields({ form, setForm, onSubmit, loading, submitLabel, isEdit, previews, onImageChange, onRemoveImage, videoPreview, onVideoChange, onRemoveVideo }) {
  return (
    <form onSubmit={onSubmit} className="space-y-3">
      {/* Image uploads - 6 slots */}
      <div>
        <Label className="text-[#0A2540] text-xs">Photos du vehicule (6 max)</Label>
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mt-1">
          {[0,1,2,3,4,5].map(idx => (
            <div key={idx}>
              {previews[idx] ? (
                <div className="relative">
                  <img src={previews[idx]} alt={`Photo ${idx+1}`} className="w-full h-20 object-cover rounded-lg border border-[#CBD5E1]" />
                  <button type="button" onClick={() => onRemoveImage(idx)} className="absolute -top-1 -right-1 w-5 h-5 bg-[#EF4444] text-white rounded-full flex items-center justify-center text-[10px]" data-testid={`remove-image-${idx}`}><X className="w-3 h-3" /></button>
                </div>
              ) : (
                <label className="flex flex-col items-center justify-center h-20 border-2 border-dashed border-[#CBD5E1] rounded-lg cursor-pointer hover:border-[#0A2540]/40 transition-colors" data-testid={`vehicle-image-upload-${idx}`}>
                  <ImagePlus className="w-5 h-5 text-[#CBD5E1]" />
                  <span className="text-[9px] text-[#64748B]">{idx === 0 ? "Principale" : `Photo ${idx+1}`}</span>
                  <input type="file" accept="image/*" onChange={e => onImageChange(idx, e)} className="hidden" />
                </label>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Video upload */}
      <div>
        <Label className="text-[#0A2540] text-xs">Vidéo de présentation <span className="text-[#94A3B8] font-normal">(optionnel, max 25 Mo)</span></Label>
        <div className="mt-1">
          {videoPreview ? (
            <div className="relative">
              <video src={videoPreview} controls className="w-full max-h-48 rounded-lg border border-[#CBD5E1] bg-black" data-testid="vehicle-video-preview">
                Votre navigateur ne supporte pas la vidéo.
              </video>
              <button type="button" onClick={onRemoveVideo} className="absolute top-1 right-1 w-7 h-7 bg-[#EF4444] text-white rounded-full flex items-center justify-center shadow-md" data-testid="remove-vehicle-video">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ) : (
            <label className="flex flex-col items-center justify-center h-24 border-2 border-dashed border-[#CBD5E1] rounded-lg cursor-pointer hover:border-[#0A2540]/40 transition-colors" data-testid="vehicle-video-upload">
              <Video className="w-6 h-6 text-[#CBD5E1] mb-1" />
              <span className="text-xs text-[#64748B]">Ajouter une vidéo (MP4, MOV, WebM)</span>
              <span className="text-[10px] text-[#94A3B8] mt-0.5">Sera visible en grand format par les clients</span>
              <input type="file" accept="video/*" onChange={onVideoChange} className="hidden" />
            </label>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div><Label className="text-[#0A2540] text-xs">Marque</Label><Input value={form.brand} onChange={e => setForm({...form, brand: e.target.value})} placeholder="Toyota" required className="h-9 border-[#CBD5E1]" data-testid="vehicle-brand" /></div>
        <div><Label className="text-[#0A2540] text-xs">Modele</Label><Input value={form.model} onChange={e => setForm({...form, model: e.target.value})} placeholder="Corolla" required className="h-9 border-[#CBD5E1]" data-testid="vehicle-model" /></div>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div><Label className="text-[#0A2540] text-xs">Annee</Label><Input type="number" value={form.year} onChange={e => setForm({...form, year: parseInt(e.target.value) || 2020})} className="h-9 border-[#CBD5E1]" data-testid="vehicle-year" /></div>
        <div><Label className="text-[#0A2540] text-xs">Couleur</Label><Input value={form.color} onChange={e => setForm({...form, color: e.target.value})} placeholder="Blanche" className="h-9 border-[#CBD5E1]" data-testid="vehicle-color" /></div>
        <div><Label className="text-[#0A2540] text-xs">Places</Label><Input type="number" value={form.seats} onChange={e => setForm({...form, seats: parseInt(e.target.value) || 1})} min={1} max={50} className="h-9 border-[#CBD5E1]" data-testid="vehicle-seats" /></div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label className="text-[#0A2540] text-xs">Plaque</Label><Input value={form.plate_number} onChange={e => setForm({...form, plate_number: e.target.value})} placeholder="LT 1234 AB" required className="h-9 border-[#CBD5E1]" data-testid="vehicle-plate" /></div>
        <div>
          <Label className="text-[#0A2540] text-xs">Type</Label>
          <Select value={form.vehicle_type} onValueChange={v => setForm({...form, vehicle_type: v})}>
            <SelectTrigger className="h-9 border-[#CBD5E1]" data-testid="vehicle-type-select"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="sedan">Berline</SelectItem><SelectItem value="suv">SUV</SelectItem><SelectItem value="4x4">4x4</SelectItem><SelectItem value="minibus">Minibus</SelectItem><SelectItem value="pickup">Pick-up</SelectItem></SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-[#0A2540] text-xs">Ville</Label>
          <Select value={form.city} onValueChange={v => setForm({...form, city: v})}>
            <SelectTrigger className="h-9 border-[#CBD5E1]" data-testid="vehicle-city"><SelectValue placeholder="Choisir la ville" /></SelectTrigger>
            <SelectContent>
              {CAMEROON_CITIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div><Label className="text-[#0A2540] text-xs">Kilometrage</Label><Input value={form.mileage} onChange={e => setForm({...form, mileage: e.target.value})} placeholder="Ex: 45,000 km" className="h-9 border-[#CBD5E1]" data-testid="vehicle-mileage" /></div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label className="text-[#0A2540] text-xs">Prix/jour (XAF)</Label><Input type="number" value={form.price_per_day} onChange={e => setForm({...form, price_per_day: parseInt(e.target.value) || 0})} min={5000} className="h-9 border-[#CBD5E1]" data-testid="vehicle-price" /></div>
        <div><Label className="text-[#0A2540] text-xs">Prix Tour de ville (XAF)</Label><Input type="number" value={form.price_city_tour} onChange={e => setForm({...form, price_city_tour: parseInt(e.target.value) || 0})} min={0} className="h-9 border-[#CBD5E1]" data-testid="vehicle-price-city" placeholder="Ex: 15000" /></div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label className="text-[#0A2540] text-xs">Prix Sortie de ville (XAF)</Label><Input type="number" value={form.price_out_of_city} onChange={e => setForm({...form, price_out_of_city: parseInt(e.target.value) || 0})} min={0} className="h-9 border-[#CBD5E1]" data-testid="vehicle-price-outskirt" placeholder="Ex: 35000" /></div>
        <div><Label className="text-[#0A2540] text-xs">Prix/heure (XAF)</Label><Input type="number" value={form.price_per_hour} onChange={e => setForm({...form, price_per_hour: parseInt(e.target.value) || 0})} min={0} className="h-9 border-[#CBD5E1]" data-testid="vehicle-price-hour" placeholder="Ex: 5000" /></div>
      </div>

      {/* Selects */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-[#0A2540] text-xs">Etat du vehicule</Label>
          <Select value={form.vehicle_condition} onValueChange={v => setForm({...form, vehicle_condition: v})}>
            <SelectTrigger className="h-9 border-[#CBD5E1]" data-testid="vehicle-condition"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="neuf">Neuf</SelectItem><SelectItem value="bon_etat">Bon etat</SelectItem><SelectItem value="acceptable">Acceptable</SelectItem></SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[#0A2540] text-xs">Carburant</Label>
          <Select value={form.fuel_policy} onValueChange={v => setForm({...form, fuel_policy: v})}>
            <SelectTrigger className="h-9 border-[#CBD5E1]" data-testid="vehicle-fuel"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="included">Inclus</SelectItem><SelectItem value="excluded">A la charge du client</SelectItem></SelectContent>
          </Select>
        </div>
      </div>

      {/* Toggle options */}
      <div className="grid grid-cols-2 gap-2">
        <ToggleOption label="Climatisation" value={form.air_conditioning} onChange={v => setForm({...form, air_conditioning: v})} testId="vehicle-ac" />
        <ToggleOption label="Musique" value={form.music} onChange={v => setForm({...form, music: v})} testId="vehicle-music" />
        <ToggleOption label="Chargeur telephone" value={form.phone_charger} onChange={v => setForm({...form, phone_charger: v})} testId="vehicle-charger" />
      </div>

      {/* Vehicle status (driver-set) — a live booking automatically overrides this. */}
      {isEdit && (
        <div>
          <Label className="text-[#0A2540] text-xs">Statut du vehicule</Label>
          <Select value={form.operational_status_manual || "available"} onValueChange={v => setForm({...form, operational_status_manual: v})}>
            <SelectTrigger className="h-9 border-[#CBD5E1]" data-testid="vehicle-status-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="available"><span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-[#10B981]" /> Disponible</span></SelectItem>
              <SelectItem value="on_trip"><span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-[#EAB308]" /> En course</span></SelectItem>
              <SelectItem value="unavailable"><span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-[#EF4444]" /> Indisponible</span></SelectItem>
            </SelectContent>
          </Select>
          <p className="text-[10px] text-[#64748B] mt-1">Astuce : si vous avez une course active, le statut &quot;En course&quot; s&apos;impose automatiquement.</p>
        </div>
      )}

      <Button type="submit" disabled={loading} className="w-full bg-[#0A2540] text-white hover:bg-[#081b2e] font-semibold" data-testid="submit-vehicle-btn">
        {loading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
        {!loading && (isEdit ? <Edit2 className="w-4 h-4 mr-2" /> : <Plus className="w-4 h-4 mr-2" />)}
        {submitLabel}
      </Button>
    </form>
  );
}
